/**
 * Sprint 18 Block E T18.22 — TR Bankalar Birliği listesi + EFT kodları.
 *
 * Veri kaynağı: TCMB Bankalar Birliği EFT kodları (resmi).
 * Kapsam: 36 ana banka (mevduat + katılım + yatırım). Eksik bankalar Sprint
 * closure öncesi enrichment ile tamamlanabilir (T18.X backlog).
 *
 * @see https://www.tbb.org.tr/tr/bankacilik/banka-bilgileri/banka-listesi/
 * @see apps/api/prisma/schema.prisma — Company.bankAccountCode
 */

export interface Bank {
  /** TR Bankalar Birliği 4-hane EFT kodu (ör. "0046" Garanti BBVA). */
  code: string;
  /** TR resmi banka adı. */
  name: string;
  /** Kısa pazar adı (UI dropdown). */
  shortName: string;
  /** Banka tipi: mevduat (klasik), katılım (faizsiz), yatırım (kalkınma). */
  type: 'DEPOSIT' | 'PARTICIPATION' | 'INVESTMENT';
}

export const BANKS: ReadonlyArray<Bank> = [
  // Mevduat bankaları (büyükten küçüğe, market share)
  { code: '0010', name: 'Türkiye Cumhuriyeti Ziraat Bankası A.Ş.', shortName: 'Ziraat Bankası', type: 'DEPOSIT' },
  { code: '0012', name: 'Türkiye Halk Bankası A.Ş.', shortName: 'Halkbank', type: 'DEPOSIT' },
  { code: '0015', name: 'Türkiye Vakıflar Bankası T.A.O.', shortName: 'VakıfBank', type: 'DEPOSIT' },
  { code: '0032', name: 'Akbank T.A.Ş.', shortName: 'Akbank', type: 'DEPOSIT' },
  { code: '0046', name: 'Türkiye Garanti Bankası A.Ş.', shortName: 'Garanti BBVA', type: 'DEPOSIT' },
  { code: '0062', name: 'Türkiye İş Bankası A.Ş.', shortName: 'İş Bankası', type: 'DEPOSIT' },
  { code: '0064', name: 'Türk Ekonomi Bankası A.Ş.', shortName: 'TEB', type: 'DEPOSIT' },
  { code: '0067', name: 'Yapı ve Kredi Bankası A.Ş.', shortName: 'Yapı Kredi', type: 'DEPOSIT' },
  { code: '0092', name: 'QNB Finansbank A.Ş.', shortName: 'QNB Finansbank', type: 'DEPOSIT' },
  { code: '0102', name: 'Anadolubank A.Ş.', shortName: 'Anadolubank', type: 'DEPOSIT' },
  { code: '0103', name: 'Fibabanka A.Ş.', shortName: 'Fibabanka', type: 'DEPOSIT' },
  { code: '0108', name: 'Şekerbank T.A.Ş.', shortName: 'Şekerbank', type: 'DEPOSIT' },
  { code: '0109', name: 'Burgan Bank A.Ş.', shortName: 'Burgan Bank', type: 'DEPOSIT' },
  { code: '0111', name: 'HSBC Bank A.Ş.', shortName: 'HSBC', type: 'DEPOSIT' },
  { code: '0112', name: 'ING Bank A.Ş.', shortName: 'ING', type: 'DEPOSIT' },
  { code: '0116', name: 'ICBC Turkey Bank A.Ş.', shortName: 'ICBC Turkey', type: 'DEPOSIT' },
  { code: '0117', name: 'Bank Mellat', shortName: 'Bank Mellat', type: 'DEPOSIT' },
  { code: '0118', name: 'J.P. Morgan Chase Bank N.A.', shortName: 'JPMorgan', type: 'DEPOSIT' },
  { code: '0134', name: 'Denizbank A.Ş.', shortName: 'DenizBank', type: 'DEPOSIT' },
  { code: '0137', name: 'Odeabank A.Ş.', shortName: 'Odeabank', type: 'DEPOSIT' },
  { code: '0143', name: 'MUFG Bank Turkey A.Ş.', shortName: 'MUFG Bank', type: 'DEPOSIT' },
  { code: '0146', name: 'Bank of China Turkey A.Ş.', shortName: 'Bank of China', type: 'DEPOSIT' },
  { code: '0217', name: 'Aktif Yatırım Bankası A.Ş.', shortName: 'N Kolay (Aktif Bank)', type: 'DEPOSIT' },

  // Katılım bankaları (faizsiz finans)
  { code: '0120', name: 'Vakıf Katılım Bankası A.Ş.', shortName: 'Vakıf Katılım', type: 'PARTICIPATION' },
  { code: '0123', name: 'Albaraka Türk Katılım Bankası A.Ş.', shortName: 'Albaraka Türk', type: 'PARTICIPATION' },
  { code: '0124', name: 'Kuveyt Türk Katılım Bankası A.Ş.', shortName: 'Kuveyt Türk', type: 'PARTICIPATION' },
  { code: '0125', name: 'Türkiye Finans Katılım Bankası A.Ş.', shortName: 'Türkiye Finans', type: 'PARTICIPATION' },
  { code: '0126', name: 'Ziraat Katılım Bankası A.Ş.', shortName: 'Ziraat Katılım', type: 'PARTICIPATION' },
  { code: '0127', name: 'Türkiye Emlak Katılım Bankası A.Ş.', shortName: 'Emlak Katılım', type: 'PARTICIPATION' },
  { code: '0128', name: 'Hayat Finans Katılım Bankası A.Ş.', shortName: 'Hayat Finans', type: 'PARTICIPATION' },

  // Yatırım + kalkınma bankaları
  { code: '0203', name: 'Türkiye İhracat Kredi Bankası A.Ş.', shortName: 'Eximbank', type: 'INVESTMENT' },
  { code: '0206', name: 'Türkiye Sınai Kalkınma Bankası A.Ş.', shortName: 'TSKB', type: 'INVESTMENT' },
  { code: '0209', name: 'İller Bankası A.Ş.', shortName: 'İlbank', type: 'INVESTMENT' },
  { code: '0210', name: 'İstanbul Takas ve Saklama Bankası A.Ş.', shortName: 'Takasbank', type: 'INVESTMENT' },
  { code: '0211', name: 'Pasha Yatırım Bankası A.Ş.', shortName: 'Pasha Yatırım', type: 'INVESTMENT' },
  { code: '0213', name: 'Diler Yatırım Bankası A.Ş.', shortName: 'Diler Yatırım', type: 'INVESTMENT' },
];

const BY_CODE = new Map<string, Bank>(BANKS.map((b) => [b.code, b]));

/** Dropdown listesi (4-hane code + shortName ile). */
export function getBanksList(): ReadonlyArray<Bank> {
  return BANKS;
}

/** EFT 4-hane koda göre banka. */
export function getBankByCode(code: string): Bank | undefined {
  return BY_CODE.get(code);
}

/** Sadece mevduat bankaları (KOBI'lerin çoğu burada). */
export function getDepositBanks(): ReadonlyArray<Bank> {
  return BANKS.filter((b) => b.type === 'DEPOSIT');
}

/** Sadece katılım bankaları (faizsiz tercih edenler). */
export function getParticipationBanks(): ReadonlyArray<Bank> {
  return BANKS.filter((b) => b.type === 'PARTICIPATION');
}

/** Kod geçerli mi (BANKS listesinde mevcut). */
export function isValidBankCode(code: string): boolean {
  return BY_CODE.has(code);
}

/**
 * IBAN'dan banka kodunu çıkarır.
 * TR IBAN format: TR{2-check}{5-bank-code}{1-reserve}{16-account} → toplam 26 hane.
 * EFT kodu IBAN'ın 5-9 karakterleri (5-hane); ilk 4 hane Bankalar Birliği EFT, son hane reserve.
 *
 * @returns 4-hane EFT kodu veya null
 */
export function extractBankCodeFromIban(iban: string): string | null {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  if (!/^TR\d{24}$/.test(cleaned)) return null;
  // Karakter 4-7 (0-indexed: 4,5,6,7) — 4-hane EFT kodu
  // TR + 2 check + 4 bank + 1 reserve + 16 account
  return cleaned.slice(4, 8);
}
